import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-8 border-b border-gray-200">
        <div className="max-w-[1800px] mx-auto flex justify-between items-center">
          <Link to="/" className="font-['Syne'] font-extrabold text-2xl tracking-tight">
            CUE.PO
          </Link>
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-8">Privacy Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: February 2025</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">1. Information We Collect</h2>
              <p className="text-gray-600 mb-4">
                When you place an order on CUE.PO, we collect information necessary to fulfill your order and provide our services:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Personal Information:</strong> Name, email address, shipping address</li>
                <li><strong>Order Information:</strong> The goals you enter for your custom poster, product selections, order history</li>
                <li><strong>Payment Information:</strong> Processed securely through Stripe - we do not store your credit card details</li>
                <li><strong>Usage Data:</strong> How you interact with our website to improve user experience</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Process and fulfill your poster orders</li>
                <li>Generate your custom poster design with your goals</li>
                <li>Send order confirmations and shipping updates</li>
                <li>Communicate about your orders and respond to inquiries</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Information Sharing</h2>
              <p className="text-gray-600 mb-4">
                We share your information only with trusted third parties necessary to fulfill your order:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Printful:</strong> Our print-on-demand partner who prints and ships your poster</li>
                <li><strong>Stripe:</strong> Our payment processor for secure transactions</li>
                <li><strong>Cloudinary:</strong> For secure image hosting of your custom poster design</li>
              </ul>
              <p className="text-gray-600 mt-4">
                We never sell your personal information to third parties for marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Data Security</h2>
              <p className="text-gray-600">
                We implement industry-standard security measures to protect your personal information. 
                All payment processing is handled by Stripe, which is PCI-DSS compliant. 
                Your poster designs are stored securely and are only accessible for order fulfillment purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Your Rights</h2>
              <p className="text-gray-600 mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt out of marketing communications</li>
              </ul>
              <p className="text-gray-600 mt-4">
                To exercise these rights, please contact us at privacy@cue.po
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Cookies</h2>
              <p className="text-gray-600">
                We use essential cookies to enable basic website functionality, such as maintaining your 
                shopping session. We do not use tracking cookies for advertising purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Changes to This Policy</h2>
              <p className="text-gray-600">
                We may update this Privacy Policy from time to time. We will notify you of any significant 
                changes by posting the new policy on this page with an updated revision date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Contact Us</h2>
              <p className="text-gray-600">
                If you have questions about this Privacy Policy, please contact us at:{" "}
                <a href="mailto:privacy@cue.po" className="text-[#002FA7] hover:underline">
                  privacy@cue.po
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
